import React, { useState, useEffect } from 'react';

const VideoCollection: React.FC = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [batchUrls, setBatchUrls] = useState('');
  const [batchResults, setBatchResults] = useState<any[]>([]);

  // 解析批量链接
  const parseBatchUrls = (text: string): string[] => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const urls: string[] = [];
    const douyinPatterns = [
      /https?:\/\/.*douyin\.com\/video\//,
      /https?:\/\/.*douyin\.com\/note\//,
      /https?:\/\/v\.douyin\.com\//,
      /https?:\/\/www\.iesdouyin\.com\//
    ];
    
    for (const line of lines) {
      // 从每行中提取URL
      const urlMatch = line.match(/https?:\/\/[^\s]+/);
      if (urlMatch) {
        const url = urlMatch[0];
        const isDouyinUrl = douyinPatterns.some(pattern => pattern.test(url));
        if (isDouyinUrl) {
          urls.push(url);
        }
      }
    }
    return urls;
  };

  // 批量下载抖音视频
  const downloadBatchDouyinVideos = async () => {
    const urls = parseBatchUrls(batchUrls);
    if (urls.length === 0) {
      setError('未检测到有效的抖音链接，请每行粘贴一个链接');
      return;
    }
    
    try {
      setIsUploading(true);
      setError(null);
      setSuccessMessage(`📥 正在批量处理 ${urls.length} 个链接...`);
      setBatchResults([]);
      
      const response = await fetch('/api/videos/download/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ urls }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setBatchResults(result.results || []);
        
        const msg = `✅ 批量处理完成！\n成功: ${result.success} | 已存在: ${result.duplicate} | 失败: ${result.failed}`;
        setSuccessMessage(msg);
        setBatchUrls('');
      } else {
        const errorData = await response.json();
        setError(errorData.message || '批量处理失败');
      }
    } catch (err) {
      setError('网络错误，批量处理失败');
    } finally {
      setIsUploading(false);
    }
  };

  // 下载抖音视频（单个，保留兼容）
  const downloadDouyinVideo = async (url: string) => {
    try {
      setIsUploading(true);
      setError(null);
      setSuccessMessage(null);
      
      const response = await fetch('/api/videos/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('视频下载任务已创建:', result.id);
        
        // 处理去重提示
        if (result.status === 'exists') {
          setSuccessMessage(`🔄 视频已存在: ${result.title || '抖音视频'}\n跳过重复下载`);
          setIsUploading(false);
          return;
        }
        
        // 处理队列状态
        const isQueued = result.status === 'queued' && result.queuePosition > 1;
        if (isQueued) {
          setSuccessMessage(`📥 已加入下载队列，位置: ${result.queuePosition}/${result.queueLength}`);
        }
        
        // 轮询检查下载状态
        const checkStatus = async () => {
          const statusResponse = await fetch(`/api/videos/download/${result.id}/status`);
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            if (statusData.status === 'completed') {
              // 下载完成
              setIsUploading(false);
              const successMsg = '✅ 视频下载完成！';
              setSuccessMessage(successMsg);
              console.log('采集成功，准备跳转到作品列表...');
              // 立即显示消息，1.5秒后跳转
              setTimeout(() => {
                console.log('跳转到作品列表');
                window.location.href = '/admin';
              }, 1500);
            } else if (statusData.status === 'failed') {
              // 下载失败
              setError(statusData.message || '视频下载失败，请稍后重试');
              setIsUploading(false);
              // 3秒后自动返回作品列表
              setTimeout(() => {
                window.location.href = '/admin';
              }, 3000);
            } else {
              // 继续轮询
              setTimeout(checkStatus, 2000);
            }
          } else {
            setError('检查下载状态失败');
            setIsUploading(false);
            // 3秒后自动返回作品列表
            setTimeout(() => {
              window.location.href = '/admin';
            }, 3000);
          }
        };
        
        // 开始轮询
        setTimeout(checkStatus, 2000);
        
      } else {
        const errorData = await response.json();
        setError(errorData.message || '视频下载失败');
        setIsUploading(false);
        // 3秒后自动返回作品列表
        setTimeout(() => {
          window.location.href = '/admin';
        }, 3000);
      }
    } catch (err) {
      setError('网络错误');
      setIsUploading(false);
      // 3秒后自动返回作品列表
      setTimeout(() => {
        window.location.href = '/admin';
      }, 3000);
    }
  };

  // 处理文件上传
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    console.log(`准备上传 ${files.length} 个视频文件`);
    for (let i = 0; i < files.length; i++) {
      formData.append('videos', files[i]);
    }

    try {
      const response = await fetch('/api/videos/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok || response.status === 207) {
        const result = await response.json();
        setUploadProgress(100);
        
        // 清除之前的错误和成功消息
        setError(null);
        setSuccessMessage(null);
        
        if (result.count > 0) {
          const successMsg = `✅ 成功上传 ${result.count} 个视频文件！`;
          setSuccessMessage(successMsg);
          console.log('上传成功，准备跳转到作品列表...');
          // 立即显示消息，1.5秒后跳转
          setTimeout(() => {
            console.log('跳转到作品列表');
            window.location.href = '/admin';
          }, 1500);
        }
        
        if (result.errors && result.errors.length > 0) {
          const errorMsg = `失败的文件：\n${result.errors.join('\n')}`;
          setError(errorMsg);
          // 3秒后自动返回作品列表
          setTimeout(() => {
            window.location.href = '/admin';
          }, 3000);
        }
        
        // 如果只有错误没有成功，也返回作品列表
        if (result.count === 0 && result.errors && result.errors.length > 0) {
          setTimeout(() => {
            window.location.href = '/admin';
          }, 3000);
        }
      } else {
        const errorData = await response.json();
        const errorMsg = errorData.message || '上传失败';
        setError(errorMsg);
        // 3秒后自动返回作品列表
        setTimeout(() => {
          window.location.href = '/admin';
        }, 3000);
      }
    } catch (err) {
      const errorMsg = '上传过程中发生错误';
      setError(errorMsg);
      // 3秒后自动返回作品列表
      setTimeout(() => {
        window.location.href = '/admin';
      }, 3000);
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  return (
    <div className="h-screen bg-gray-900 text-white p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">📥 视频采集</h1>
        
        {/* 上传区域 */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">📤 批量上传视频</h2>
          <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <p className="text-blue-300 text-sm">
              🎯 <strong>最佳实践建议</strong>：为了获得最佳视频质量和100%成功率，
              <strong>强烈推荐使用手动上传方式</strong>！
            </p>
            <p className="text-blue-200 text-xs mt-1">
              在抖音App中点击"分享" → "保存到相册"，然后上传到本系统。
            </p>
          </div>
          <p className="text-gray-400 mb-4 text-sm">支持同时选择多个视频文件，最大单个文件100MB</p>
          <div className="flex flex-col space-y-4">
            <div className="flex items-center space-x-4">
              <label className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded cursor-pointer transition-colors font-medium">
                📁 选择多个视频文件
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
              
              <div className="flex flex-col space-y-3 flex-1">
                <div className="text-yellow-400 text-sm bg-yellow-900/30 p-2 rounded border border-yellow-700/50">
                  💡 <strong>推荐使用手动上传</strong>：由于抖音反爬虫机制，自动下载可能失败或获取到低质量视频。
                  建议在抖音App中使用"保存到相册"功能，然后通过上方的"选择多个视频文件"上传。
                </div>
                
                {/* 批量链接输入 */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">批量粘贴抖音链接（每行一个）</label>
                  <textarea
                    value={batchUrls}
                    onChange={(e) => setBatchUrls(e.target.value)}
                    placeholder="https://v.douyin.com/xxxxx&#10;https://v.douyin.com/yyyyy&#10;https://v.douyin.com/zzzzz"
                    rows={4}
                    disabled={isUploading}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {parseBatchUrls(batchUrls).length > 0 && `检测到 ${parseBatchUrls(batchUrls).length} 个有效链接`}
                    </span>
                    <button
                      onClick={downloadBatchDouyinVideos}
                      disabled={isUploading || parseBatchUrls(batchUrls).length === 0}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:opacity-50 px-4 py-2 rounded transition-colors font-medium text-sm"
                    >
                      {isUploading ? '⏳ 处理中...' : '📥 批量下载'}
                    </button>
                  </div>
                </div>
                
                {/* 批量结果展示 */}
                {batchResults.length > 0 && (
                  <div className="bg-gray-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <div className="text-xs text-gray-400 mb-2">处理结果:</div>
                    <div className="space-y-1">
                      {batchResults.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {item.status === 'queued' && <span className="text-green-400">✅</span>}
                          {item.status === 'exists' && <span className="text-yellow-400">🔄</span>}
                          {(item.status === 'failed' || item.status === 'invalid') && <span className="text-red-400">❌</span>}
                          <span className="text-gray-300 truncate flex-1">{item.title || item.url.substring(0, 40)}...</span>
                          <span className={`text-xs ${
                            item.status === 'queued' ? 'text-green-400' :
                            item.status === 'exists' ? 'text-yellow-400' :
                            'text-red-400'
                      }`}>
                            {item.status === 'queued' ? '已加入队列' :
                             item.status === 'exists' ? '已存在' :
                             item.status === 'invalid' ? '无效链接' : '失败'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {isUploading && (
              <div className="flex items-center space-x-2">
                <div className="w-32 bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span>{uploadProgress}%</span>
              </div>
            )}
          </div>
          {successMessage && (
            <div className="mt-4 p-3 bg-green-900 text-green-200 rounded">
              {successMessage}
            </div>
          )}
          {error && (
            <div className="mt-4 p-3 bg-red-900 text-red-200 rounded">
              {error}
            </div>
          )}
        </div>
        
        {/* 导航链接 */}
        <div className="flex justify-center space-x-4">
          <a 
            href="/admin" 
            className="inline-block bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            ← 返回作品列表
          </a>
          <a 
            href="/" 
            className="inline-block bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            🎬 返回视频播放器
          </a>
        </div>
      </div>
    </div>
  );
};

export default VideoCollection;